import React, { useEffect, useMemo, useState } from 'react';

import { SearchInput } from 'src/components/SearchInput';
import { AudienceType, CampaignAudience } from 'src/api/adminNotifications';
import { AdminPlatformUser, fetchPlatformUsers, formatUserDisplayName } from 'src/api/adminUsers';
import { Option, fetchCountryOptions } from 'src/api/reference';
import { useSearchQueryValue } from 'src/hooks/useDebouncedSearch';
import { KYC_STATUS_OPTIONS } from 'src/utils/constants';

import {
  CheckboxRow,
  Chip,
  ChipList,
  Field,
  FieldLabel,
  MetaLine,
  OptionList,
  OptionRow,
  RadioChip,
  RadioRow,
} from './styled';

const AUDIENCE_TYPES: { value: AudienceType; label: string }[] = [
  { value: 'all', label: 'All users' },
  { value: 'country', label: 'By nationality' },
  { value: 'kyc', label: 'By KYC status' },
  { value: 'users', label: 'Specific users' },
];

type PickedUser = { id: string; label: string };

type AudiencePickerProps = {
  value: CampaignAudience;
  // eslint-disable-next-line no-unused-vars
  onChange: (next: CampaignAudience) => void;
  /** Labels for the picked users, kept by the parent so chips survive re-renders. */
  pickedUsers: PickedUser[];
  // eslint-disable-next-line no-unused-vars
  onPickedUsersChange: (next: PickedUser[]) => void;
};

/**
 * The four audience shapes the API accepts, each with its own control:
 * country and KYC status are multi-selects with chips, specific users is a
 * search-and-pick against the users list.
 */
const AudiencePicker: React.FC<AudiencePickerProps> = ({ value, onChange, pickedUsers, onPickedUsersChange }) => {
  const [countries, setCountries] = useState<Option[]>([]);
  const [countrySearch, setCountrySearch] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const debouncedUserSearch = useSearchQueryValue(userSearch, 400);
  const [userResults, setUserResults] = useState<AdminPlatformUser[]>([]);
  const [searchingUsers, setSearchingUsers] = useState(false);

  useEffect(() => {
    let active = true;
    fetchCountryOptions()
      .then((options) => {
        if (active) setCountries(options);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (value.type !== 'users' || !debouncedUserSearch) {
      setUserResults([]);
      return undefined;
    }
    let active = true;
    setSearchingUsers(true);
    // Email prefix when it looks like one, otherwise a name prefix.
    const field = debouncedUserSearch.includes('@') || !debouncedUserSearch.includes(' ') ? 'email' : 'name';
    fetchPlatformUsers({ page: 1, limit: 10, field, value: debouncedUserSearch })
      .then((res) => {
        if (active) setUserResults(res.users);
      })
      .catch(() => {
        if (active) setUserResults([]);
      })
      .finally(() => {
        if (active) setSearchingUsers(false);
      });
    return () => {
      active = false;
    };
  }, [debouncedUserSearch, value.type]);

  const selectedCountries = value.country_codes ?? [];
  const selectedKyc = value.kyc_statuses ?? [];
  const selectedUserIds = value.user_ids ?? [];

  const visibleCountries = useMemo(() => {
    const term = countrySearch.trim().toLowerCase();
    const list = term ? countries.filter((c) => c.label.toLowerCase().includes(term)) : countries;
    return list.slice(0, 60);
  }, [countries, countrySearch]);

  const setType = (type: AudienceType) => onChange({ type });

  const toggleCountry = (code: string) => {
    const next = selectedCountries.includes(code)
      ? selectedCountries.filter((c) => c !== code)
      : [...selectedCountries, code];
    onChange({ ...value, type: 'country', country_codes: next });
  };

  const toggleKyc = (status: string) => {
    const next = selectedKyc.includes(status) ? selectedKyc.filter((s) => s !== status) : [...selectedKyc, status];
    onChange({ ...value, type: 'kyc', kyc_statuses: next });
  };

  const addUser = (user: AdminPlatformUser) => {
    if (selectedUserIds.includes(user.id)) return;
    onChange({ ...value, type: 'users', user_ids: [...selectedUserIds, user.id] });
    onPickedUsersChange([...pickedUsers, { id: user.id, label: user.email || formatUserDisplayName(user) }]);
  };

  const removeUser = (id: string) => {
    onChange({ ...value, type: 'users', user_ids: selectedUserIds.filter((u) => u !== id) });
    onPickedUsersChange(pickedUsers.filter((u) => u.id !== id));
  };

  const countryLabel = (code: string) => countries.find((c) => c.value === code)?.label ?? code;

  return (
    <>
      <Field>
        <FieldLabel>Audience</FieldLabel>
        <RadioRow>
          {AUDIENCE_TYPES.map((option) => (
            <RadioChip
              key={option.value}
              type="button"
              $active={value.type === option.value}
              onClick={() => setType(option.value)}
            >
              {option.label}
            </RadioChip>
          ))}
        </RadioRow>
      </Field>

      {value.type === 'country' && (
        <Field>
          <FieldLabel>Countries</FieldLabel>
          {selectedCountries.length > 0 && (
            <ChipList>
              {selectedCountries.map((code) => (
                <Chip key={code}>
                  {countryLabel(code)}
                  <button type="button" aria-label={`Remove ${code}`} onClick={() => toggleCountry(code)}>
                    ×
                  </button>
                </Chip>
              ))}
            </ChipList>
          )}
          <SearchInput inline showIcon placeholder="Search countries…" value={countrySearch} onChange={setCountrySearch} />
          <OptionList>
            {visibleCountries.map((c) => (
              <OptionRow
                key={c.value}
                type="button"
                $selected={selectedCountries.includes(c.value)}
                onClick={() => toggleCountry(c.value)}
              >
                <span>{c.label}</span>
                <small>{selectedCountries.includes(c.value) ? 'Selected' : ''}</small>
              </OptionRow>
            ))}
            {visibleCountries.length === 0 && (
              <OptionRow type="button" disabled>
                <span>{countries.length ? 'No match' : 'Loading countries…'}</span>
              </OptionRow>
            )}
          </OptionList>
          <MetaLine>Nationality is the country on the user&apos;s KYC document.</MetaLine>
        </Field>
      )}

      {value.type === 'kyc' && (
        <Field>
          <FieldLabel>KYC status</FieldLabel>
          <RadioRow>
            {KYC_STATUS_OPTIONS.map((option) => (
              <RadioChip
                key={option.value}
                type="button"
                $active={selectedKyc.includes(option.value)}
                onClick={() => toggleKyc(option.value)}
              >
                {option.label}
              </RadioChip>
            ))}
          </RadioRow>
          <CheckboxRow>
            <input
              type="checkbox"
              checked={Boolean(value.kyc_in_progress)}
              onChange={(e) => onChange({ ...value, type: 'kyc', kyc_in_progress: e.target.checked })}
            />
            Also include users with a verification in progress (opened, started or submitted, not yet decided)
          </CheckboxRow>
        </Field>
      )}

      {value.type === 'users' && (
        <Field>
          <FieldLabel>Users ({selectedUserIds.length})</FieldLabel>
          {pickedUsers.length > 0 && (
            <ChipList>
              {pickedUsers.map((user) => (
                <Chip key={user.id}>
                  {user.label}
                  <button type="button" aria-label={`Remove ${user.label}`} onClick={() => removeUser(user.id)}>
                    ×
                  </button>
                </Chip>
              ))}
            </ChipList>
          )}
          <SearchInput
            inline
            showIcon
            placeholder="Search by email or name…"
            value={userSearch}
            onChange={setUserSearch}
          />
          {(userResults.length > 0 || searchingUsers) && (
            <OptionList>
              {searchingUsers && userResults.length === 0 ? (
                <OptionRow type="button" disabled>
                  <span>Searching…</span>
                </OptionRow>
              ) : (
                userResults.map((user) => (
                  <OptionRow
                    key={user.id}
                    type="button"
                    $selected={selectedUserIds.includes(user.id)}
                    onClick={() => addUser(user)}
                  >
                    <span>
                      {formatUserDisplayName(user)} <small>{user.email}</small>
                    </span>
                    <small>{selectedUserIds.includes(user.id) ? 'Added' : 'Add'}</small>
                  </OptionRow>
                ))
              )}
            </OptionList>
          )}
        </Field>
      )}
    </>
  );
};

export default AudiencePicker;
